/**
 * money.ts — จำนวนเงินด้วย Decimal (ห้าม IEEE-754 float)
 *
 * กติกา (CLAUDE.md / CONTEXT.md §7):
 * - จำนวนเงินเก็บเป็น decimal string (map กับ Postgres `numeric`)
 * - การคำนวณทำผ่าน decimal.js เท่านั้น
 * - สกุลเงินหลัก THB มี 2 ตำแหน่ง (สตางค์); เก็บสกุล/อัตราแลกเปลี่ยนเมื่อไม่ใช่ THB
 * - GP%/EBITDA%/NP% เมื่อฐานเป็นศูนย์ต้องคืน null (UI/export แสดง `—`)
 */
import Decimal from 'decimal.js';

// precision สูงพอสำหรับผลรวมจำนวนมาก; rounding เกิดเฉพาะตอน quantize/present
Decimal.set({ precision: 34, rounding: Decimal.ROUND_HALF_UP });

export type MoneyInput = string | number | Decimal;

/** จำนวนตำแหน่งทศนิยมของ THB (สตางค์) */
export const THB_SCALE = 2;

/**
 * แปลงค่าเป็น Decimal อย่างปลอดภัย
 * ปฏิเสธ number ที่ไม่ finite และ string ที่ parse ไม่ได้ เพื่อกันข้อมูลเงินเสีย
 */
export function toDecimal(value: MoneyInput): Decimal {
  if (value instanceof Decimal) {
    if (!value.isFinite()) throw new RangeError('จำนวนเงินไม่ถูกต้อง (ไม่ finite)');
    return value;
  }
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) throw new RangeError('จำนวนเงินไม่ถูกต้อง (ไม่ finite)');
    // number → string ก่อนเข้า Decimal เพื่อรักษาความตั้งใจของผู้เรียก
    // (แนะนำให้ส่ง string สำหรับเงินเสมอ)
    return new Decimal(value.toString());
  }
  const trimmed = value.trim();
  if (trimmed === '') throw new RangeError('จำนวนเงินว่าง');
  const d = new Decimal(trimmed);
  if (!d.isFinite()) throw new RangeError(`จำนวนเงินไม่ถูกต้อง: ${value}`);
  return d;
}

export function addMoney(...values: MoneyInput[]): Decimal {
  return values.reduce<Decimal>((acc, v) => acc.plus(toDecimal(v)), new Decimal(0));
}

export function sumMoney(values: readonly MoneyInput[]): Decimal {
  return addMoney(...values);
}

export function subtractMoney(a: MoneyInput, b: MoneyInput): Decimal {
  return toDecimal(a).minus(toDecimal(b));
}

/** คูณจำนวนเงินด้วยตัวคูณ (เช่น ต้นทุนต่อหน่วย × จำนวนชิ้น) */
export function multiplyMoney(amount: MoneyInput, factor: MoneyInput): Decimal {
  return toDecimal(amount).times(toDecimal(factor));
}

export function negateMoney(value: MoneyInput): Decimal {
  return toDecimal(value).negated();
}

/** ปัดจำนวนเงินให้เหลือ scale ที่กำหนด (default THB 2 ตำแหน่ง), ROUND_HALF_UP */
export function quantizeMoney(value: MoneyInput, scale: number = THB_SCALE): Decimal {
  return toDecimal(value).toDecimalPlaces(scale, Decimal.ROUND_HALF_UP);
}

/** serialize เป็น string สำหรับเก็บ/ส่งผ่าน API (map กับ numeric) */
export function moneyToString(value: MoneyInput, scale: number = THB_SCALE): string {
  return quantizeMoney(value, scale).toFixed(scale);
}

export function isZeroMoney(value: MoneyInput): boolean {
  return toDecimal(value).isZero();
}

/**
 * อัตราส่วน numerator/denominator
 * คืน `null` เมื่อ denominator เป็นศูนย์ (กติกา zero-denominator) — ห้ามคืน 0 หรือ throw
 */
export function ratio(numerator: MoneyInput, denominator: MoneyInput): Decimal | null {
  const d = toDecimal(denominator);
  if (d.isZero()) return null;
  return toDecimal(numerator).div(d);
}

/**
 * เปอร์เซ็นต์ = numerator/denominator × 100
 * ใช้กับ GP%, EBITDA%, NP%; คืน `null` เมื่อฐานเป็นศูนย์ (แสดง `—`)
 */
export function percentage(numerator: MoneyInput, denominator: MoneyInput): Decimal | null {
  const r = ratio(numerator, denominator);
  return r === null ? null : r.times(100);
}

/** format เปอร์เซ็นต์เป็นข้อความ; null → `—` (ตามกติกา) */
export function formatPercent(value: Decimal | null, dp = 1): string {
  if (value === null) return '—';
  return `${value.toDecimalPlaces(dp, Decimal.ROUND_HALF_UP).toFixed(dp)}%`;
}

/** format เงินไทยพร้อมตัวคั่นหลักพันและ 2 ตำแหน่ง */
export function formatTHB(value: MoneyInput, opts?: { withSymbol?: boolean }): string {
  const q = quantizeMoney(value);
  const [intPart, fracPart = '00'] = q.abs().toFixed(THB_SCALE).split('.');
  const grouped = (intPart ?? '0').replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  const sign = q.isNegative() ? '-' : '';
  const symbol = opts?.withSymbol ? '฿' : '';
  return `${sign}${symbol}${grouped}.${fracPart}`;
}

export { Decimal };
